/**
 * WYSIWYG Product Embed Handler
 * Opens the shared product search modal from the Quill toolbar and inserts
 * the picked product as a card embedded in the article content.
 *
 * Reuses the SAME search input/modal as the single "Target Product" linker
 * (post-product-linker) rather than a second parallel instance — a separate
 * dedicated instance turned out to be unreliable (the shared search-menu
 * module reads its query from the ORIGINAL trigger input, not the modal's own
 * keyword field, in ways that only line up correctly for the first/only
 * instance on a page). Mutual exclusion between "set as linked product" and
 * "embed in content" is handled via a data-wysiwyg-embed-pending flag on the
 * shared input — see ProductLinkerHandler.handleProductSelected's guard.
 */

import { WysiwygProductEmbedRender } from "./wysiwyg-product-embed-render";
import { WysiwygProductEmbedBlotUtils } from "./wysiwyg-product-embed-blot-utils";
import type { ProductLinkerProduct } from "../../post/linker/product-linker-api";

interface QuillSelectionRange {
    index: number;
    length: number;
}

interface QuillLike {
    getSelection(focus?: boolean): QuillSelectionRange | null;
    getLength(): number;
    setSelection(index: number, length: number, source?: string): void;
    insertEmbed(index: number, name: string, value: unknown, source?: string): void;
}

class WysiwygProductEmbedHandler {
    private readonly quill: QuillLike;
    private readonly searchInput: HTMLInputElement;
    private savedIndex: number | null = null;

    public constructor(quill: QuillLike, searchInput: HTMLInputElement) {
        this.quill = quill;
        this.searchInput = searchInput;
    }

    /**
     * Toolbar button click: remember where the caret was, then open the
     * picker (via the shared search input's own focus-triggers-modal wiring).
     */
    public handleToolbarClick(): void {
        const range = this.quill.getSelection(true);
        this.savedIndex = range ? range.index : this.quill.getLength();
        this.searchInput.dataset.wysiwygEmbedPending = "1";
        this.searchInput.focus();
    }

    /**
     * search-menu:product-selected fired on the shared post-product-linker
     * input. ProductLinkerHandler.handleProductSelected is bound to the same
     * event/input and checks the same flag to skip when we own the pick —
     * the flag is cleared via a microtask (not synchronously here) so BOTH
     * listeners still see a consistent value during this single dispatch,
     * regardless of which one happens to run first.
     */
    public handleProductSelected(event: Event): void {
        if (this.searchInput.dataset.wysiwygEmbedPending !== "1") {
            return;
        }
        queueMicrotask(() => delete this.searchInput.dataset.wysiwygEmbedPending);

        const detail = (event as CustomEvent<{ product?: ProductLinkerProduct }>).detail;
        const product = detail?.product;
        if (!product) return;

        const value = WysiwygProductEmbedRender.buildEmbedValue(product);
        if (!value) return;

        const index = this.savedIndex ?? this.quill.getLength();
        this.quill.insertEmbed(index, WysiwygProductEmbedBlotUtils.formatName, value, "user");
        this.quill.setSelection(index + 1, 0, "user");
        this.savedIndex = null;
    }
}

export { WysiwygProductEmbedHandler };
